/* ==========================================================================
   FLC Forge — Product Form & Variant Selects
   ========================================================================== */

class VariantSelects extends HTMLElement {
  connectedCallback() {
    this.productHandle = this.dataset.productHandle;
    this.productJSON   = JSON.parse(document.querySelector(`[data-product-json="${this.productHandle}"]`)?.textContent || '{}');
    this.inputs        = this.querySelectorAll('select[name^="options"], input[name^="options"]');

    this.inputs.forEach(input => {
      input.addEventListener('change', () => this.updateVariant());
    });

    // Init from URL or first available
    this.setFromURL();
  }

  get optionGroups() {
    const seen = new Set();
    return [...this.inputs].filter(input => {
      if (seen.has(input.name)) return false;
      seen.add(input.name);
      return true;
    });
  }

  get currentOptions() {
    return this.optionGroups.map(input =>
      input.type === 'radio'
        ? this.querySelector(`input[name="${input.name}"]:checked`)?.value
        : input.value
    ).filter(Boolean);
  }

  findVariant(options) {
    return this.productJSON.variants?.find(v =>
      v.options.every((opt, i) => opt === options[i])
    );
  }

  updateVariant() {
    const options = this.currentOptions;
    const variant = this.findVariant(options);

    document.querySelectorAll('[data-variant-id]').forEach(el => {
      el.value = variant?.id || '';
    });

    this.updatePrices(variant);
    this.updateAvailability(variant);
    this.updateMedia(variant);
    this.updateURL(variant);

    FLC.dispatch('flc:variant:changed', { variant, options }, this);
  }

  updatePrices(variant) {
    const priceEl   = document.querySelector('[data-product-price]');
    const compareEl = document.querySelector('[data-product-compare-price]');
    const priceWrap = document.querySelector('[data-price-wrap]');
    const btnPrice  = document.querySelector('[data-add-to-cart-price]');

    if (!variant) return;

    if (priceEl)   priceEl.innerHTML   = FLC.formatMoney(variant.price);
    if (compareEl) compareEl.innerHTML = variant.compare_at_price > variant.price
      ? FLC.formatMoney(variant.compare_at_price)
      : '';

    if (btnPrice) {
      btnPrice.dataset.unitPrice = variant.price;
      const qty = parseInt(document.querySelector('[data-form-quantity]')?.value || '1', 10);
      btnPrice.textContent = FLC.formatMoney(variant.price * qty);
    }

    priceWrap?.classList.toggle('price--on-sale', variant.compare_at_price > variant.price);
  }

  updateAvailability(variant) {
    const addBtn           = document.querySelector('[data-add-to-cart-btn]');
    const btnText          = document.querySelector('[data-add-to-cart-text]');
    const hideForComingSoon = !!document.querySelector('product-form[data-hide-sold-out-coming-soon]');

    if (!addBtn) return;

    if (!variant) {
      addBtn.disabled = true;
      addBtn.hidden   = false;
      if (btnText) btnText.textContent = window.flcForge.i18n?.unavailable ?? btnText.textContent;
    } else if (!variant.available) {
      if (hideForComingSoon) {
        addBtn.hidden = true;
      } else {
        addBtn.disabled = true;
        addBtn.hidden   = false;
        if (btnText) btnText.textContent = window.flcForge.i18n?.sold_out ?? btnText.textContent;
      }
    } else {
      addBtn.disabled = false;
      addBtn.hidden   = false;
      if (btnText) btnText.textContent = window.flcForge.i18n?.add_to_cart ?? btnText.textContent;
    }
  }

  updateMedia(variant) {
    if (!variant?.featured_media) return;
    const mediaId = variant.featured_media.id;
    const mediaEl = document.querySelector(`[data-media-id="${mediaId}"]`);
    if (mediaEl) {
      // Scroll gallery to this media
      mediaEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
  }

  updateURL(variant) {
    if (!variant) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  setFromURL() {
    const params  = new URLSearchParams(window.location.search);
    const varId   = params.get('variant');
    if (!varId) { this.updateVariant(); return; }

    const variant = this.productJSON.variants?.find(v => v.id == varId);
    if (!variant) { this.updateVariant(); return; }

    const groups = this.optionGroups;
    variant.options.forEach((opt, i) => {
      const input = groups[i];
      if (!input) return;
      if (input.type === 'radio') {
        const radio = this.querySelector(`input[name="${input.name}"][value="${CSS.escape(opt)}"]`);
        if (radio) radio.checked = true;
      } else {
        input.value = opt;
      }
    });

    this.updateVariant();
  }
}

customElements.define('variant-selects', VariantSelects);

/* Product Form (add to cart)
   ========================================================================== */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form       = this.querySelector('form');
    this.submitBtn  = this.querySelector('[data-add-to-cart-btn]');
    this.cartType   = window.flcForge.cartType || 'drawer';

    this.form?.addEventListener('submit', (e) => this.onSubmit(e));
  }

  async onSubmit(e) {
    e.preventDefault();
    this.setLoading(true);

    const formData = new FormData(this.form);
    const variantId = formData.get('id');
    if (!variantId) return;

    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && value !== '') properties[match[1]] = value;
    }

    const payload = {
      id: variantId,
      quantity: parseInt(formData.get('quantity') || '1', 10)
    };
    if (Object.keys(properties).length) payload.properties = properties;

    try {
      const data = await FLC.fetchJSON(window.flcForge.routes.cart_add_url, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      FLC.dispatch('flc:cart:refresh');

      if (this.cartType === 'drawer') {
        FLC.dispatch('flc:cart:open');
      } else if (this.cartType === 'page') {
        window.location.href = window.flcForge.routes.cart_url;
      }

      window.liveRegion?.announce(window.flcForge.i18n?.added_to_cart || 'Added to cart');
    } catch (err) {
      console.error('Add to cart failed:', err);
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(state) {
    if (this.submitBtn) {
      this.submitBtn.disabled = state;
      this.submitBtn.setAttribute('aria-busy', state);
    }
  }
}

customElements.define('product-form', ProductForm);

/* Product Media Gallery
   ========================================================================== */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.track       = this.querySelector('[data-gallery-track]');
    this.thumbBtns   = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.prevBtn     = this.querySelector('[data-gallery-prev]');
    this.nextBtn     = this.querySelector('[data-gallery-next]');
    this.zoomOverlay = this.querySelector('[data-gallery-zoom]');
    this.zoomSources = [...this.querySelectorAll('[data-zoom-src]')];

    this.currentIndex     = 0;
    this.currentZoomIndex = 0;
    this._wasDragging     = false;

    // Thumbnail clicks
    this.thumbBtns.forEach(btn => {
      btn.addEventListener('click', () => this.goTo(parseInt(btn.dataset.galleryThumb, 10)));
    });

    // Gallery arrow clicks
    this.prevBtn?.addEventListener('click', () => this.goTo(this.currentIndex - 1));
    this.nextBtn?.addEventListener('click', () => this.goTo(this.currentIndex + 1));

    // Sync on manual swipe/scroll
    if (this.track) {
      // touchmove fires before scroll on iOS — stop media as soon as a swipe gesture starts
      this.track.addEventListener('touchmove', () => {
        [...this.track.children].forEach(slide => this._stopPanelMedia(slide));
      }, { passive: true });

      this.track.addEventListener('scroll', FLC.debounce(() => this._syncFromScroll(), 80), { passive: true });
    }

    // Editorial mobile pagination
    this.editorialTrack = this.querySelector('[data-editorial-track]');
    this.editorialBars  = [...this.querySelectorAll('[data-editorial-bar]')];
    if (this.editorialTrack && this.editorialBars.length) {
      // Stop media when swiping through editorial on mobile
      this.editorialTrack.addEventListener('touchmove', () => {
        [...this.editorialTrack.querySelectorAll('.product-gallery__grid-item')].forEach(item => this._stopPanelMedia(item));
      }, { passive: true });

      this.editorialTrack.addEventListener('scroll', FLC.debounce(() => {
        const i = Math.round(this.editorialTrack.scrollLeft / this.editorialTrack.clientWidth);
        this.editorialBars.forEach((bar, j) => bar.classList.toggle('is-active', j === i));
      }, 60), { passive: true });
    }

    // Zoom setup
    if ('zoomEnabled' in this.dataset) {
      this._initZoom();
    }

    // Variant change → jump to variant media
    document.addEventListener('flc:variant:changed', e => {
      if (e.detail.variant?.featured_media) {
        const mediaId = e.detail.variant.featured_media.id;
        const slides  = [...this.querySelectorAll('[data-media-id]')];
        const idx     = slides.findIndex(el => el.dataset.mediaId == mediaId);
        if (idx !== -1) this.goTo(idx);
      }
    });

    this._syncArrows(0);
  }

  /* Gallery navigation ---------------------------------------------------- */
  get _slideCount() { return this.track ? this.track.children.length : 0; }

  goTo(index) {
    if (!this.track) return;
    const i = Math.max(0, Math.min(index, this._slideCount - 1));
    if (i !== this.currentIndex) this._stopPanelMedia(this.track.children[this.currentIndex]);
    this.track.children[i]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    this.currentIndex = i;
    this._syncThumbs(i);
    this._syncArrows(i);
  }

  _syncFromScroll() {
    if (!this.track || !this.track.clientWidth) return;
    const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
    if (i === this.currentIndex) return;
    this._stopPanelMedia(this.track.children[this.currentIndex]);
    this.currentIndex = i;
    this._syncThumbs(i);
    this._syncArrows(i);
  }

  _syncThumbs(i) {
    this.thumbBtns.forEach((btn, j) => {
      btn.classList.toggle('is-active', j === i);
      btn.setAttribute('aria-pressed', String(j === i));
    });
  }

  _syncArrows(i) {
    if (this.prevBtn) this.prevBtn.disabled = i === 0;
    if (this.nextBtn) this.nextBtn.disabled = i >= this._slideCount - 1;
  }

  /* Zoom ------------------------------------------------------------------ */
  _initZoom() {
    this.zoomScroll  = this.querySelector('[data-zoom-scroll]');
    this.zoomDots    = [...this.querySelectorAll('[data-zoom-dot]')];
    this.zoomPanels  = [...this.querySelectorAll('[data-zoom-panel]')];

    // Open zoom on image click
    this.zoomSources.forEach((el, i) => {
      el.addEventListener('click', () => {
        if (!this._wasDragging) this._openZoom(i);
      });
    });

    // Close
    this.querySelector('[data-zoom-close]')?.addEventListener('click', () => this._closeZoom());
    this.zoomOverlay?.addEventListener('click', e => {
      if (e.target === this.zoomOverlay) this._closeZoom();
    });
    document.addEventListener('keydown', e => {
      if (e.key !== 'Escape') return;
      if (this.zoomOverlay?.getAttribute('aria-hidden') === 'false') this._closeZoom();
    });

    // Dot clicks
    this.zoomDots.forEach(btn => {
      btn.addEventListener('click', () => this._scrollZoomTo(parseInt(btn.dataset.zoomDot, 10), false));
    });

    // Update active dot as user scrolls — centre-line trigger works for any image height
    if (this.zoomScroll && this.zoomPanels.length > 1) {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          const activeIndex = parseInt(entry.target.dataset.zoomPanel, 10);
          this._syncZoomDots(activeIndex);
          this.zoomPanels.forEach((panel, i) => {
            if (i !== activeIndex) this._stopPanelMedia(panel);
          });
        });
      }, { root: this.zoomScroll, threshold: 0, rootMargin: '-50% 0px -50% 0px' });
      this.zoomPanels.forEach(panel => observer.observe(panel));
    }
  }

  _openZoom(index) {
    if (!this.zoomOverlay) return;
    // Populate all panel images on first open
    this.zoomPanels.forEach((panel, i) => {
      const img = panel.querySelector('[data-zoom-panel-img]');
      if (img && !img.getAttribute('src') && this.zoomSources[i]) {
        img.src = this.zoomSources[i].dataset.zoomSrc;
        img.alt = this.zoomSources[i].querySelector('img')?.alt || '';
      }
    });
    this.zoomOverlay.setAttribute('aria-hidden', 'false');
    FLC.openOverlay('gallery-zoom');
    requestAnimationFrame(() => this._scrollZoomTo(index, true));
  }

  _closeZoom() {
    if (!this.zoomOverlay) return;
    this._stopAllZoomMedia();
    this.zoomOverlay.setAttribute('aria-hidden', 'true');
    if (this.zoomScroll) this.zoomScroll.scrollTop = 0;
    FLC.closeOverlay('gallery-zoom');
  }

  _stopPanelMedia(panel) {
    if (!panel) return;
    panel.querySelectorAll('video').forEach(v => { v.pause(); v.load(); });
    panel.querySelectorAll('iframe').forEach(iframe => {
      const src = iframe.src;
      iframe.src = '';
      iframe.src = src;
    });
  }

  _stopAllZoomMedia() {
    this.zoomPanels?.forEach(panel => this._stopPanelMedia(panel));
  }

  _scrollZoomTo(index, instant = false) {
    if (!this.zoomScroll) return;
    const panel = this.zoomPanels[index];
    if (!panel) return;
    const top = panel.offsetTop;
    if (instant) {
      this.zoomScroll.scrollTop = top;
    } else {
      this.zoomScroll.scrollTo({ top, behavior: 'smooth' });
    }
    this._syncZoomDots(index);
  }

  _syncZoomDots(i) {
    this.currentZoomIndex = i;
    this.zoomDots.forEach((dot, j) => dot.classList.toggle('is-active', j === i));
  }
}

customElements.define('product-gallery', ProductGallery);

/* Quantity Steppers
   ========================================================================== */
(function initQuantitySteppers() {
  function updateBtnPrice(qty) {
    const btnPrice = document.querySelector('[data-add-to-cart-price]');
    if (!btnPrice) return;
    const unitPrice = parseInt(btnPrice.dataset.unitPrice || '0', 10);
    if (!unitPrice) return;
    btnPrice.textContent = FLC.formatMoney(unitPrice * qty);
  }

  function syncQty(input) {
    const targetId = input.dataset.qtySync;
    if (!targetId) return;
    const target = document.getElementById(targetId);
    if (target) target.value = input.value;
    updateBtnPrice(parseInt(input.value || '1', 10));
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-quantity-change]');
    if (!btn) return;
    const wrap  = btn.closest('.product__qty-wrap');
    const input = wrap?.querySelector('input[type="number"]');
    if (!input) return;
    const delta = parseInt(btn.dataset.quantityChange, 10);
    const min   = parseInt(input.min || '1', 10);
    input.value = Math.max(min, parseInt(input.value || '1', 10) + delta);
    syncQty(input);
  });

  document.addEventListener('change', (e) => {
    if ('qtySync' in e.target.dataset) syncQty(e.target);
  });
})();
